/* eslint-disable react/prop-types */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
    ArrowRightOutlined,
    BulbOutlined,
    CheckOutlined,
    DownOutlined,
    GlobalOutlined,
    PictureOutlined,
} from "@ant-design/icons";
import { useUser } from "../../../contexts/UserContext/UserContext";
import { useToast } from "../../../contexts/ToastContext";
import { useProductTour } from "../../../components/product-tour/ProductTourProvider";
import { isTourCompleted } from "../../../components/product-tour/tourStorage";
import {
    isBookingSiteShared,
    isGettingStartedCollapsed,
    markBookingSiteShared,
    setGettingStartedCollapsed,
} from "./gettingStartedStorage";
import getBookingSiteUrl from "./bookingSiteUrl";
import cn from "../../../utilities/tailwind-merge";

const TOTAL_STEPS = 3;

/* A step card. When it has an action the whole card is the hit target, so the
   link text is an affordance rather than the only thing you can click. */
const ChecklistCard = ({ done, icon, title, description, actionLabel, doneLabel, onAction }) => {
    const Tag = onAction ? "button" : "div";

    return (
        <Tag
            {...(onAction ? { type: "button", onClick: onAction } : {})}
            className={cn(
                // Icon sits beside the text on phones to keep the stacked cards short.
                "flex h-full items-start gap-3 rounded-2xl border p-4 text-left transition-all sm:flex-col sm:p-5",
                done ? "border-emerald-100 bg-emerald-50/70" : "border-gray-100 bg-white shadow-sm",
                onAction &&
                    "hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5D5DF6]",
                onAction && !done && "hover:border-[#5D5DF6]",
            )}>
            <span
                className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-base",
                    done ? "bg-emerald-500 text-white" : "bg-[#F0F0FE] text-[#5D5DF6]",
                )}>
                {done ? <CheckOutlined /> : icon}
            </span>

            <span className="flex min-w-0 flex-1 flex-col gap-3 self-stretch">
                <span className="block">
                    <span className="block text-[15px] font-semibold text-gray-900">{title}</span>
                    <span className="mt-1 block text-[13px] leading-relaxed text-gray-500">{description}</span>
                </span>

                {/* Pinned to the bottom so the three cards line up even when one of
                    them has finished and no longer offers an action. */}
                <span className="mt-auto pt-1 text-[13px] font-semibold">
                    {actionLabel ? (
                        <span
                            className={cn(
                                "inline-flex items-center gap-1.5",
                                done ? "text-gray-600" : "text-[#5D5DF6]",
                            )}>
                            {actionLabel}
                            <ArrowRightOutlined className="text-[11px]" />
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-emerald-600">
                            <CheckOutlined className="text-[11px]" />
                            {doneLabel}
                        </span>
                    )}
                </span>
            </span>
        </Tag>
    );
};

const GettingStartedCard = () => {
    const { t } = useTranslation("dashboard");
    const { state, myCompanyData } = useUser();
    const history = useHistory();
    const toast = useToast();
    const { startTour } = useProductTour();

    const userId = state?.user?.id;
    const companyId = myCompanyData?.id;

    const [bookingSiteShared, setBookingSiteShared] = useState(() => isBookingSiteShared(userId));

    const logoUploaded = Boolean(myCompanyData?.logo?.light || myCompanyData?.logo?.dark);
    const tourCompleted = isTourCompleted(userId);

    const completedCount = [logoUploaded, bookingSiteShared, tourCompleted].filter(Boolean).length;
    const allDone = completedCount === TOTAL_STEPS;

    const [expanded, setExpanded] = useState(() => !isGettingStartedCollapsed(userId) && !allDone);

    // Once every step is done there's nothing left to act on, so the card
    // disappears entirely instead of lingering as a permanent "3 of 3" badge.
    if (allDone) {
        return null;
    }

    const toggleExpanded = () => {
        const next = !expanded;
        setExpanded(next);
        setGettingStartedCollapsed(userId, !next);
    };

    const handleShareLink = async () => {
        // Read companyId at call time: it arrives from an async company fetch, so
        // anything captured on first render is still undefined.
        const url = getBookingSiteUrl(companyId);
        if (!url) return;

        try {
            await navigator.clipboard.writeText(url);
            toast.success({ message: t("linkCopied") });
        } catch (_error) {
            /* clipboard may be unavailable, ignore */
        }

        markBookingSiteShared(userId);
        setBookingSiteShared(true);
    };

    const items = [
        {
            key: "logo",
            done: logoUploaded,
            icon: <PictureOutlined />,
            title: t("uploadLogo"),
            description: t("uploadLogoDescription"),
            actionLabel: logoUploaded ? null : t("uploadNow"),
            onAction: logoUploaded ? null : () => history.push("/settings"),
        },
        {
            key: "bookingSite",
            done: bookingSiteShared,
            icon: <GlobalOutlined />,
            title: t("shareBookingSite"),
            description: t("shareBookingSiteDescription"),
            actionLabel: t("shareLink"),
            onAction: handleShareLink,
        },
        {
            key: "tour",
            done: tourCompleted,
            icon: <BulbOutlined />,
            title: t("takeProductTour"),
            description: t("takeProductTourDescription"),
            actionLabel: tourCompleted ? t("takeTourAgain") : t("startTour"),
            onAction: startTour,
        },
    ];

    const progressLabel = t("gettingStartedProgress", { completed: completedCount, total: TOTAL_STEPS });

    return (
        <div className="shrink-0 rounded-2xl border border-[#E7E7FA] bg-[#F7F7FE] p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#5D5DF6]">
                        {t("gettingStarted")}
                        {!expanded ? (
                            <span className="ml-2 font-semibold normal-case tracking-normal text-gray-500">
                                {progressLabel}
                            </span>
                        ) : null}
                    </p>

                    {expanded ? (
                        <>
                            {/* text-left / p-0 undo the unscoped `h3` rule in App.css,
                                which centres every h3 and pads it 20px top and bottom. */}
                            <h3 className="mt-1.5 p-0 text-left text-xl font-semibold text-gray-900">
                                {t("gettingStartedTitle")}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">{t("gettingStartedDescription")}</p>
                        </>
                    ) : null}
                </div>

                <button
                    type="button"
                    onClick={toggleExpanded}
                    aria-expanded={expanded}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-gray-500 transition-colors hover:bg-white hover:text-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5D5DF6]">
                    {expanded ? t("hide") : t("show")}
                    <DownOutlined className={cn("text-[10px] transition-transform", expanded && "rotate-180")} />
                </button>
            </div>

            {expanded ? (
                <>
                    <div className="mt-4 flex items-center gap-3">
                        <div
                            role="progressbar"
                            aria-valuenow={completedCount}
                            aria-valuemin={0}
                            aria-valuemax={TOTAL_STEPS}
                            aria-label={progressLabel}
                            className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E3E3F3]">
                            <div
                                className="h-full rounded-full bg-[#5D5DF6] transition-[width] duration-500 ease-out"
                                style={{ width: `${(completedCount / TOTAL_STEPS) * 100}%` }}
                            />
                        </div>
                        <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-500">
                            {progressLabel}
                        </span>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:mt-5 sm:grid-cols-3 sm:gap-4">
                        {items.map((item) => (
                            <ChecklistCard key={item.key} {...item} doneLabel={t("completed")} />
                        ))}
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default GettingStartedCard;
